import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import AuthForm from "./AuthForm";
import { auth, freeGame, getStoredUser } from "../lib/api";

const INVITE = /^\/(multiplayer\/match|w|p)\//;

const GuestFreeBar = ({ onAuthed, left }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="h-20" />
      <div data-testid="guest-free-bar" className="fixed bottom-0 inset-x-0 z-40 bg-[#1E293B]/95 backdrop-blur border-t border-[#334155]">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <Gift className="w-5 h-5 text-[#F59E0B] shrink-0" />
          <div className="flex-1 min-w-0 text-xs leading-snug">
            <div data-testid="guest-free-left" className="font-semibold text-white">{left > 0 ? `${left} free game${left === 1 ? "" : "s"} left` : "Your last free game"}</div>
            <div className="text-slate-400">Sign up to keep any winnings and play more.</div>
          </div>
          <button data-testid="guest-free-signup-btn" onClick={() => setOpen(true)} className="btn-green rounded-lg px-4 py-2 text-sm font-bold shrink-0">Sign up</button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-transparent border-0 p-0 w-[calc(100%-2rem)] max-w-sm shadow-none">
          <DialogTitle className="sr-only">Sign up</DialogTitle>
          <AuthForm onAuthed={(u) => { setOpen(false); freeGame.end(); onAuthed(u); }} title="Keep your winnings" subtitle="Create your free account. Anything you won in your free games stays in your balance." />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Game routes: registered players, or a guest's one free game; otherwise sign-up first.
const RequireAccount = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);

  const [failed, setFailed] = useState(false);

  const load = () => {
    setFailed(false);
    // A network/server hiccup must never look like "signed out": retry, then offer a retry button.
    auth.ensure()
      .catch(() => new Promise((r) => setTimeout(r, 800)).then(() => auth.ensure()))
      .then(setUser)
      .catch(() => { const stored = getStoredUser(); if (stored && !stored.is_guest) setUser(stored); else setFailed(true); });
  };
  useEffect(load, []);

  if (failed) {
    return (
      <div data-testid="require-account-error" className="bg-app min-h-screen flex flex-col items-center justify-center gap-3 text-slate-300 px-6 text-center">
        <p>We couldn't reach Footie Clash. Check your connection and try again.</p>
        <button data-testid="require-account-retry" onClick={load} className="btn-green rounded-lg px-5 py-2.5 font-semibold">Try again</button>
      </div>
    );
  }
  if (user === undefined) {
    return <div className="bg-app min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (user && !user.is_guest) return children;
  if (user && (!user.free_game_used || freeGame.active())) {
    freeGame.start();
    return (
      <>
        {children}
        <GuestFreeBar onAuthed={setUser} left={user.free_games_left ?? 0} />
      </>
    );
  }

  const invite = INVITE.test(pathname);
  return (
    <div data-testid="require-account" className="bg-app min-h-screen text-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="relative z-10 max-w-5xl w-full mx-auto px-4 h-14 flex items-center">
        <button data-testid="require-account-home" onClick={() => navigate("/")} className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
      </div>
      <div className="relative z-10 flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-sm">
          <AuthForm
            onAuthed={setUser}
            title={invite ? "Your mate's challenge is waiting" : "Your free games are used up"}
            subtitle={invite ? "Create your free account (or log in) and you'll jump straight into the game." : "Sign up free (or log in) to keep playing. Anything you won is kept."}
          />
        </div>
      </div>
    </div>
  );
};

export default RequireAccount;
